/**
 * Embedded Signup v4: a metade que roda no navegador.
 *
 * O fluxo tem DUAS fontes de verdade que chegam por caminhos diferentes, e é isso que
 * torna o código menos óbvio do que parece:
 *
 *   - o `code` vem no callback do `FB.login`;
 *   - o `waba_id` e o `phone_number_id` vêm por `postMessage` da janela da Meta.
 *
 * Nenhum dos dois basta sozinho, e a ordem entre eles não é garantida. Por isso aqui se
 * junta os dois e só então se resolve — em vez de assumir que a mensagem chega antes,
 * que é o que quase todo snippet de blog faz e o que quebra quando a máquina está lenta.
 *
 * ✅ Tudo isto é **v4**, e o Coexistence continua sendo ligado por `extras.featureType`:
 * o configurador oficial do App Dashboard gera, para v4 com "Integração do app WhatsApp
 * Business", exatamente `extras: { version: 'v4', featureType:
 * 'whatsapp_business_app_onboarding' }`. Sem isso o fluxo só oferece número novo, e um
 * número que já tem WhatsApp Business bate em "já está registrado". Ver
 * `docs/meta/RUNBOOK-ONBOARDING.md` §8.
 */

/** O payload que a janela da Meta manda por `postMessage`. */
export interface SinalDoSignup {
  readonly modo: 'cloud_api' | 'coexistence';
  readonly wabaId: string;
  readonly phoneNumberId: string;
  readonly businessId: string | null;
  readonly sessionId: string | null;
}

export interface ResultadoDoSignup extends SinalDoSignup {
  readonly code: string;
}

export type PassoDoSignup =
  | { readonly tipo: 'passo'; readonly step: string; readonly sessionId: string | null }
  | { readonly tipo: 'concluido'; readonly sinal: SinalDoSignup };

/**
 * Desistência e erro chegam pelo MESMO evento `CANCEL` ✅ — o que os separa é haver
 * `error_message`. Distinguir importa: desistir num passo é dado de funil, falhar é
 * incidente, e tratar os dois como "não deu" apaga justamente o que se quer medir.
 */
export class SignupInterrompido extends Error {
  constructor(
    readonly motivo: 'cancelado' | 'erro',
    readonly step: string | null,
    readonly errorCode: string | null,
    readonly sessionId: string | null,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = 'SignupInterrompido';
  }
}

interface FacebookSdk {
  init(options: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }): void;
  login(
    callback: (resposta: { authResponse?: { code?: string } | null }) => void,
    options: {
      config_id: string;
      response_type: string;
      override_default_response_type: boolean;
      extras: { version: string; featureType: string };
    },
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

let carregando: Promise<FacebookSdk> | null = null;

/**
 * Carrega e inicializa o SDK uma vez só.
 *
 * `FB.init` é idempotente do ponto de vista da Meta, mas injetar o script duas vezes
 * registra dois handlers e faz o `postMessage` chegar em duplicata — e aí o fluxo
 * resolve com dados de uma sessão e `code` de outra.
 */
export function carregarSdkDaMeta(appId: string, graphVersion: string): Promise<FacebookSdk> {
  if (carregando !== null) return carregando;

  carregando = new Promise<FacebookSdk>((resolve, reject) => {
    const pronto = (): void => {
      const fb = window.FB;
      if (fb === undefined) {
        reject(new Error('O SDK da Meta carregou sem expor window.FB'));
        return;
      }
      fb.init({ appId, autoLogAppEvents: true, xfbml: true, version: graphVersion });
      resolve(fb);
    };

    if (window.FB !== undefined) {
      pronto();
      return;
    }

    window.fbAsyncInit = pronto;

    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      carregando = null;
      reject(
        new Error(
          'Não foi possível carregar o SDK da Meta. Bloqueador de anúncios e extensões ' +
            'de privacidade costumam barrar connect.facebook.net.',
        ),
      );
    };
    document.head.appendChild(script);
  });

  return carregando;
}

/**
 * Os `FINISH` que terminam com número ✅. `FINISH_ONLY_WABA` também existe, e termina sem
 * número: o médico criou a conta e parou antes de escolher o telefone.
 */
const FIM_COM_NUMERO: Readonly<Record<string, SinalDoSignup['modo']>> = {
  FINISH: 'cloud_api',
  FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: 'coexistence',
};

/** O sinal de um `FINISH` com número, ou `null` se o evento não é um deles. */
export function sinalDoFim(evento: unknown, dados: Record<string, unknown>): SinalDoSignup | null {
  const modo = FIM_COM_NUMERO[String(evento)];
  if (modo === undefined) return null;
  const waba = dados['waba_id'];
  const phone = dados['phone_number_id'];
  if (typeof waba !== 'string' || typeof phone !== 'string') return null;

  const business = dados['business_id'];
  const sessao = dados['session_id'];
  return {
    modo,
    wabaId: waba,
    phoneNumberId: phone,
    businessId: typeof business === 'string' ? business : null,
    sessionId: typeof sessao === 'string' ? sessao : null,
  };
}

/**
 * Abre o fluxo e resolve com o que o servidor precisa para fechar a conexão.
 *
 * `aoAndar` recebe cada passo para o funil de abandono. Ele é chamado durante o fluxo,
 * não no fim — um médico que fecha a janela no passo 3 não gera nenhuma outra evidência.
 */
export function abrirEmbeddedSignup({
  appId,
  configId,
  graphVersion,
  aoAndar,
}: {
  readonly appId: string;
  readonly configId: string;
  readonly graphVersion: string;
  readonly aoAndar?: (passo: PassoDoSignup) => void;
}): Promise<ResultadoDoSignup> {
  return carregarSdkDaMeta(appId, graphVersion).then(
    (fb) =>
      new Promise<ResultadoDoSignup>((resolve, reject) => {
        let sinal: SinalDoSignup | null = null;
        let interrupcao: SignupInterrompido | null = null;
        let encerrado = false;
        let esperandoSinal: ReturnType<typeof setTimeout> | null = null;

        /**
         * O que a janela da Meta falou, para o caso de ela nao falar o que precisamos.
         *
         * "A Meta autorizou mas nao informou a conta" tem duas causas opostas e o mesmo
         * sintoma: ou nenhuma mensagem chegou — o fluxo do WhatsApp nem rodou, e o
         * problema e de configuracao —, ou chegaram varias e nenhuma era FINISH, e aí o
         * medico parou no meio. Sem distinguir, o funil nao serve para nada.
         */
        const ouvidas: string[] = [];

        const encerrar = (fn: () => void): void => {
          if (encerrado) return;
          encerrado = true;
          window.removeEventListener('message', ouvir);
          if (esperandoSinal !== null) clearTimeout(esperandoSinal);
          fn();
        };

        function ouvir(evento: MessageEvent): void {
          // A doc manda conferir a origem ✅. Sem isto, qualquer iframe da página
          // poderia forjar um `waba_id` e nos fazer conectar a conta de outro.
          let origem: string;
          try {
            origem = new URL(evento.origin).hostname;
          } catch {
            return;
          }
          if (origem !== 'facebook.com' && !origem.endsWith('.facebook.com')) return;

          let dados: Record<string, unknown>;
          try {
            const cru: unknown =
              typeof evento.data === 'string' ? JSON.parse(evento.data) : evento.data;
            if (typeof cru !== 'object' || cru === null) return;
            dados = cru as Record<string, unknown>;
          } catch {
            // A Meta manda outras mensagens nesta janela que não são JSON. Ignorar em
            // silêncio é o comportamento certo: barulho aqui esconde o evento que importa.
            return;
          }

          ouvidas.push(`${String(dados['type'] ?? '?')}/${String(dados['event'] ?? '?')}`);

          if (dados['type'] !== 'WA_EMBEDDED_SIGNUP') return;

          const corpo = (dados['data'] ?? {}) as Record<string, unknown>;
          const sessionId = typeof corpo['session_id'] === 'string' ? corpo['session_id'] : null;

          if (typeof dados['event'] === 'string' && dados['event'] in FIM_COM_NUMERO) {
            const s = sinalDoFim(dados['event'], corpo);
            if (s === null) return;
            sinal = s;
            aoAndar?.({ tipo: 'concluido', sinal: s });
            return;
          }

          if (dados['event'] === 'FINISH_ONLY_WABA') {
            interrupcao = new SignupInterrompido(
              'cancelado',
              'PHONE_NUMBER_SETUP',
              null,
              sessionId,
              'A conta do WhatsApp foi criada, mas nenhum número foi escolhido. Abra de novo e ' +
                'selecione o número do consultório.',
            );
            return;
          }

          // Desistência e erro vêm os dois como CANCEL; `error_message` é o que separa.
          const erro = corpo['error_message'];
          const step = typeof corpo['current_step'] === 'string' ? corpo['current_step'] : null;
          if (typeof erro === 'string' && erro !== '') {
            interrupcao = new SignupInterrompido(
              'erro',
              step,
              typeof corpo['error_code'] === 'string' || typeof corpo['error_code'] === 'number'
                ? String(corpo['error_code'])
                : null,
              sessionId,
              erro,
            );
            return;
          }
          if (dados['event'] === 'CANCEL') {
            interrupcao = new SignupInterrompido(
              'cancelado',
              step,
              null,
              sessionId,
              step === null
                ? 'O fluxo foi fechado antes de terminar.'
                : `Fechado no passo ${step}.`,
            );
            return;
          }

          if (step !== null) aoAndar?.({ tipo: 'passo', step, sessionId });
        }

        window.addEventListener('message', ouvir);

        fb.login(
          (resposta) => {
            const code = resposta.authResponse?.code;

            if (typeof code !== 'string' || code === '') {
              encerrar(() =>
                reject(
                  interrupcao ??
                    new SignupInterrompido(
                      'cancelado',
                      null,
                      null,
                      null,
                      'A janela da Meta fechou sem autorizar.',
                    ),
                ),
              );
              return;
            }

            // Com `code` na mão, o que pode faltar é o `postMessage`. Ele quase sempre
            // já chegou; a espera cobre o caso em que não chegou, e é limitada porque o
            // `code` vive 30 s — esperar demais entrega ao servidor um código morto.
            const concluir = (): void => {
              if (sinal === null) {
                encerrar(() =>
                  reject(
                    new Error(
                      ouvidas.length === 0
                        ? 'A Meta autorizou, mas a janela dela não emitiu nenhum evento de ' +
                            'cadastro. O fluxo do WhatsApp não chegou a rodar: confira a ' +
                            'Configuration do Embedded Signup e o config_id.'
                        : 'A Meta autorizou mas não informou a conta e o número. Eventos ' +
                            `recebidos: ${ouvidas.join(', ')}.`,
                    ),
                  ),
                );
                return;
              }
              const s: SinalDoSignup = sinal;
              encerrar(() => resolve({ ...s, code }));
            };

            if (sinal !== null) {
              concluir();
              return;
            }
            esperandoSinal = setTimeout(concluir, 8000);
          },
          {
            config_id: configId,
            response_type: 'code',
            override_default_response_type: true,
            // ✅ Como o configurador oficial gera para v4 com Coexistence. Sem o
            // `featureType`, a tela de conta existente não aparece.
            extras: { version: 'v4', featureType: 'whatsapp_business_app_onboarding' },
          },
        );
      }),
  );
}
